// react
import { MouseEvent, useContext, useState } from 'react';

// types
import { ActionButton, ErrorCause, IfrPlanVersion } from '../../../@types';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';
import { ContextIfrPlan, ContextIfrPlanVersion } from '../../pages/Project/Project.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, Box, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// utils
import { formatCurrency } from '../../../utils/currency';

// props
export interface DialogUpdateIfrPlanVersionStatusProps {
	errors?: ErrorCause[];
	isOpen: boolean;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: ({ ifrPlanVersion }: { ifrPlanVersion: IfrPlanVersion }) => void;
}

export const DialogUpdateIfrPlanVersionStatus = ({
	errors = [],
	isOpen,
	onClose,
	onSuccess,
}: DialogUpdateIfrPlanVersionStatusProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramStatus = searchParams.get('dialogStatus');

	// context
	const { setBanner } = useContext(ContextBanner);
	const { ifrPlan } = useContext(ContextIfrPlan);
	const { ifrPlanVersion } = useContext(ContextIfrPlanVersion);

	// state
	const [error, setError] = useState<string | null>(null);

	// deactivate translations
	const dialog = {
		alert: t('alert.finalizeIfrPlanVersion'),
		btnSubmit: {
			children: t('finalizeDraft'),
			color: 'primary',
		},
		i18nKey: 'dialogUpdateIfrPlanVersionStatus.finalize.content',
		title: t('finalizeDraft'),
	};

	// patch ifr plan version
	const { fetchRequest: updateIfrPlanVersion, isLoading: isLoadingUpdateIfrPlanVersion } = useFetch(
		{
			isLazy: true,
			url: `${process.env.REACT_APP_API_URL}/ifrs/plans/${ifrPlanVersion?.ifrPlan}/versions/${ifrPlanVersion?.id}`,
			options: {
				method: 'PATCH',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify({
					status: paramStatus,
				}),
			},
			onError: (error) => {
				let msg = t('error.default');

				if (error.id === 'IFR_PLAN_VERSION_UNAUTHORIZED_PATCH') {
					msg = t('error.unauthorized');
				}

				setError(msg);
			},
			onSuccess: (res) => {
				// set banner
				setBanner({
					hasClose: true,
					message: t('alert.ifrPlanFinalized', { name: ifrPlan?.name }),
					severity: 'success',
				});

				// success callback
				onSuccess?.({ ifrPlanVersion: res.data });

				// close dialog callback
				onClose?.();
			},
		}
	);

	// handlers
	const handleSubmit = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear error
		setError(null);

		// update ifr plan version
		updateIfrPlanVersion();
	};

	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear error
		setError(null);

		if (onClose) {
			onClose(e);
		}
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: handleClose,
					variant: 'text',
				},
				{
					...(dialog.btnSubmit as ActionButton),
					loading: isLoadingUpdateIfrPlanVersion,
					onClick: handleSubmit,
					variant: 'contained',
				},
			]}
			alignItems="center"
			IconCircleProps={{
				type: 'plan',
			}}
			isOpen={isOpen}
			onClose={handleClose}
			subtitle={{
				children: ifrPlan?.name,
			}}
			title={{
				children: dialog.title,
			}}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Trans
						components={{
							strong: <Typography color="brand.purple.600" component="span" fontWeight={600} />,
						}}
						i18nKey={dialog.i18nKey}
					/>
				</Grid>
				{errors.length > 0 && (
					<Grid item xs={12}>
						<Alert severity="warning">
							<Typography fontWeight={600}>{t('warnings')}</Typography>
							<Box component="ul">
								{errors.map((error, index) => {
									let errorMessage = '';
									console.log('Error:', error);

									switch (error.id) {
										case 'IFR_PLAN_CASH_IN_EXCEEDS_LOAN_AMOUNT':
											errorMessage = t('error.planCashInExceedsLoanAmount', {
												amount: formatCurrency({
													currency: ifrPlan?.currency,
													value: error.details?.loanAmount,
												}),
												loan: error.details?.loan?.code,
											});
											break;
										case 'IFR_PLAN_CATEGORY_ALLOCATION_EXCEEDED':
											errorMessage = t('error.planCategoryAllocationExceeded', {
												category: error.details?.category?.name,
											});
											break;
										case 'IFR_PLAN_COMPONENT_ALLOCATION_EXCEEDED':
											errorMessage = t('error.planComponentAllocationExceeded', {
												component: error.details?.component?.name,
											});
											break;
										case 'IFR_PLAN_CASH_OUT_CATEGORY_MONTH_EXCEEDS_CASH_IN':
											errorMessage = t('error.planCashOutCategoryExceedsCashIn', {
												month: error.details?.month?.name || error.details?.field,
											});
											break;
										case 'IFR_PLAN_CASH_OUT_COMPONENT_MONTH_EXCEEDS_CASH_IN':
											errorMessage = t('error.planCashOutComponentExceedsCashIn', {
												month: error.details?.month?.name || error.details?.field,
											});
											break;
										default:
											errorMessage = t('error.default');
											break;
									}

									return (
										<li key={`error-${index}`}>
											<Typography>{errorMessage}</Typography>
										</li>
									);
								})}
							</Box>
						</Alert>
					</Grid>
				)}
				<Grid item xs={12}>
					<Alert severity="info">{dialog.alert}</Alert>
				</Grid>
				{error && (
					<Grid item xs={12}>
						<Alert severity="error">{error}</Alert>
					</Grid>
				)}
			</Grid>
		</DialogActions>
	);
};
